import { computed, ref, shallowRef } from 'vue'
import { debouncedWatch } from '@vueuse/core'
import { isArray } from '@bd/vue3-utils'
import { API_ORDER_TYPE } from '@/constant/api'
import { useConditions } from '@/hooks/useConditions'
import type { ConditionItem } from '-/Base/ConditionBox'
import { message, type PaginationProps, type TableProps } from 'ant-design-vue'

type UseTableOptions<T = any> = {
  /** 条件配置 */
  conditionConfig?: ConditionItem[]
  /** 列表接口请求函数 */
  api: (params: BasicPageReq) => Promise<BasicPageRes<T>>
  /** 初始每页条数 */
  pageSize?: number
  /** 请求失败回调（用于提示 message 等） */
  onError?: (err: unknown) => void
}

export function useTable<T>(options: UseTableOptions<T>) {
  const { pageSize = 20, conditionConfig = [], api, onError } = options
  const loading = ref(false)
  const dataSource = shallowRef<T[]>([])
  const originDataSource = shallowRef<BasicPageRes<T>>()
  const ordersObj = ref<Record<string, BasicOrderType>>({})
  const conditionObj = ref<Record<string, any>>({})

  const getPageSize = () => pagination.value.pageSize ?? pageSize
  const getCurrent = () => pagination.value.current ?? 1

  const calcLastPage = (total: number, pageSize: number) => Math.max(1, Math.ceil(total / pageSize))

  const showTotal: PaginationProps['showTotal'] = (total: number) => {
    const pageSize = getPageSize()
    const pages = calcLastPage(total, pageSize)
    const current = getCurrent()
    return `第 ${current} / ${pages} 页 | 共 ${total} 条`
  }

  const pagination = ref<PaginationProps>({
    current: 1,
    pageSize,
    total: 0,
    showSizeChanger: true,
    showTotal
  })

  const { genConditions } = useConditions(conditionConfig)

  const buildFetchParams = (): BasicPageReq => {
    const orders = Object.entries(ordersObj.value).map(([orderKeyName, orderType]) => ({ orderKeyName, orderType }))
    const conditions = genConditions(conditionObj.value)

    const params: BasicPageReq = {
      page: getCurrent(),
      pageSize: getPageSize()
    }

    if (orders.length) params.orders = orders
    if (conditions.length) params.conditions = conditions
    return params
  }

  const fetchParams = computed<BasicPageReq>(() => buildFetchParams())

  const normalizeSorterToOrders = (sorter: any): Record<string, BasicOrderType> => {
    const sorters = isArray(sorter) ? sorter : [sorter]
    return sorters.reduce(
      (acc, cur) => {
        if (cur?.order && cur?.field) {
          acc[String(cur.field)] = cur.order === 'ascend' ? API_ORDER_TYPE.ASC : API_ORDER_TYPE.DESC
        }
        return acc
      },
      {} as Record<string, BasicOrderType>
    )
  }

  const applyTablePaginationChange = (pag: PaginationProps) => {
    const nextPageSize = pag.pageSize ?? getPageSize()

    let nextCurrent = pag.current ?? getCurrent()
    if (nextPageSize !== pagination.value.pageSize) nextCurrent = 1

    pagination.value.pageSize = nextPageSize
    pagination.value.current = nextCurrent
  }

  const applyResponse = (res: BasicPageRes<T>) => {
    originDataSource.value = res
    const total = res.total ?? 0
    const pageSize = getPageSize()
    const lastPage = calcLastPage(total, pageSize)

    pagination.value.total = total

    // total=0：强制归一到第一页
    if (total === 0) {
      pagination.value.current = 1
      dataSource.value = []
      return
    }

    // 当前页超出范围：回退并等待 watcher 重新拉取正确页
    const current = getCurrent()
    if (current > lastPage) {
      pagination.value.current = lastPage
      return
    }

    dataSource.value = res.data ?? []
  }

  const handleRequestError = (err: unknown) => {
    onError?.(err)
    message.error('数据加载失败，请稍后重试')
  }

  const refresh = async () => {
    if (loading.value) return
    loading.value = true

    try {
      const res = await api(fetchParams.value)
      applyResponse(res)
    } catch (err) {
      handleRequestError(err)
    } finally {
      loading.value = false
    }
  }

  const resetToFirstPageIfNeeded = () => {
    if (getCurrent() !== 1) pagination.value.current = 1
  }

  const handleConditionChange = (val: Record<string, any>) => {
    conditionObj.value = val
    resetToFirstPageIfNeeded()
  }

  const handleTableChange: TableProps['onChange'] = (pag, _filters, sorter) => {
    applyTablePaginationChange(pag)
    ordersObj.value = normalizeSorterToOrders(sorter)
  }

  debouncedWatch(fetchParams, refresh, { immediate: true, deep: true, debounce: 100 })

  return {
    loading,
    dataSource,
    pagination,
    ordersObj,
    conditionObj,
    fetchParams,
    refresh,
    handleConditionChange,
    handleTableChange,
    originDataSource
  }
}
